import { useRef, useState } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import theme from '../../../assets/theme';
import useDismissResponse from '../../../hooks/useDismissResponse';
import { useMetrics } from '../../../hooks/useMetrics';
import useMixpanelFlag from '../../../hooks/useMixpanelFlag';
import { useWindowSize } from '../../../hooks/useWindowSize';
import useWordcloudResults from '../../../hooks/useWordcloudResults';
import { Button } from '../../_atoms/Button';
import BaseModal from '../../_molecules/BaseModal';
import LiveEmptyState from '../../_molecules/LiveEmptyState';
import { createWordCloud, WORDCLOUD_DIV } from './wordcloudUtil';

const Wordcloud = ({
  roundId,
  gameId,
  staticData,
  disableActions,
  isAuthenticated,
  uniqueId,
  refetchData,
}: {
  roundId?: string;
  gameId?: string;
  disableActions?: boolean;
  isAuthenticated: boolean;
  staticData?: { id: string; label: string; count: number }[];
  uniqueId?: string;
  refetchData?: boolean;
}) => {
  const { t } = useTranslation('Host');
  const useNextWordcloud = useMixpanelFlag('wordcloudnext');
  const wordcloudDivId = `${WORDCLOUD_DIV}-${roundId}-${gameId}-${uniqueId}`;
  const { data, cacheKey } = useWordcloudResults(
    gameId,
    roundId,
    true,
    isAuthenticated,
    refetchData,
  );
  const { dismissResponse } = useDismissResponse(gameId ? gameId : '', roundId, cacheKey);
  const wordcloudRef = useRef<HTMLDivElement>(null);

  const metrics = useMetrics();
  const [showModal, setShowModal] = useState(false);
  const [selectedWord, setSelectedWord] = useState<
    { label: string; id: string; count: number } | undefined
  >();
  const [width, _] = useWindowSize();
  useEffect(() => {
    // wordcloud for fetched from game
    if (wordcloudRef.current && data) {
      const wordCloud = createWordCloud({
        useNext: useNextWordcloud,
        wordcloudDivId,
        onWordClick: word => {
          if (!disableActions) {
            setSelectedWord(word);
            setShowModal(true);
          }
        },
        nodes: data.map(d => ({
          id: d.responseId,
          count: d.score,
          label: d.response.response,
        })),
        clientWidth: wordcloudRef.current.clientWidth,
        clientHeight: wordcloudRef.current.clientHeight,
      });
      wordCloud.render();

      return () => wordCloud.destroy();
    }
  }, [data, wordcloudDivId, width, useNextWordcloud, disableActions]);

  // Wordcloud for passed dowd variables
  useEffect(() => {
    if (wordcloudRef.current && staticData) {
      const wordCloud = createWordCloud({
        useNext: useNextWordcloud,
        nodes: staticData,
        clientWidth: wordcloudRef.current.clientWidth,
        clientHeight: wordcloudRef.current.clientHeight,
        wordcloudDivId,
      });
      wordCloud.render();
      return () => wordCloud.destroy();
    }
  }, [staticData, useNextWordcloud, wordcloudDivId]);
  return (
    <>
      {!disableActions && (
        <BaseModal
          visible={!!selectedWord && showModal}
          onCancel={() => setShowModal(false)}
          title={`Dismiss ${selectedWord?.label}?`}
        >
          <p>{t('Dismissing this response will remove it from the word cloud')}</p>
          <div
            style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '5px' }}
          >
            <Button
              onClick={() => setShowModal(false)}
              styles={{ gridColumn: '2/3' }}
              type="ghost"
            >
              {' '}
              {t('Cancel')}
            </Button>
            <Button
              type="primary"
              onClick={e => {
                e.preventDefault();
                if (!!selectedWord) {
                  if (isAuthenticated) {
                    metrics.logEvent('Wordcloud.response.dismiss');
                  }
                  dismissResponse({ responseId: selectedWord.id, dismiss: true });
                  setShowModal(false);
                }
              }}
            >
              {/* TODO: translation */}
              Dismiss
            </Button>
          </div>
        </BaseModal>
      )}
      {data?.length === 0 && (
        <LiveEmptyState
          title={t('No words submitted')}
          style={{ margin: '10px', width: '100%' }}
        />
      )}
      {staticData && (
        <div
          ref={wordcloudRef}
          id={wordcloudDivId}
          style={{
            width: '90%',
            height: '95%',
            backgroundColor: theme.colors.backgroundTransparent,
            borderRadius: '10px',
          }}
        ></div>
      )}
      {data && data.length >= 1 && (
        <div
          ref={wordcloudRef}
          id={wordcloudDivId}
          style={{
            width: '90%',
            height: '95%',
            backgroundColor: theme.colors.backgroundTransparent,
            borderRadius: '10px',
          }}
        ></div>
      )}
    </>
  );
};

export default Wordcloud;
